/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import {
  Form,
  Col,
  ToggleButton,
  ButtonGroup,
  Button,
  Row,
  InputGroup
} from 'react-bootstrap';
import axios from 'axios';
import apiClient from 'api/client';
import ToggleWithDropdown from './FormFields/ToggleWithDropdown';
import DynamicDropDown from './FormFields/DropDown';
import CheckBox from './FormFields/CheckBox';
import File from './FormFields/File';
import Dob from './FormFields/Dob';
import MapModel from './FormFields/Mapmodel';
import * as turf from '@turf/turf';
import { fetchAddressFromOla } from './FormFields/Mapmodel/services/fetchAddressfromOla';
import { FormLabel } from './FormFields/FormLabel';
import ErrorFeedback from './FormFields/ErrorFeedback';
import { debounce } from 'lodash';
import RangeDatePicker from 'screens/common/RageDatePicker';
import MonthDayRange from './FormFields/MonthDayRange';
import PasswordInput from './FormFields/Mapmodel/PasswordInput';
import offlineDBService from 'db/dexieDB';
import CalculationField from './FormFields/CalculationField';
import { useAuth } from 'hooks/useAuth';

// Helper function to detect mobile device
function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
const DynamicNormalForm = ({
  metadata,
  onToggleChange,
  handleDateRangeChange,
  handleMonthDayChange,
  mode,
  hideFields,
  modalDataMap
}) => {
  const apiKey = process.env.REACT_APP_OLA_API_KEY;
  // Use useController for more complex form control

  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues
  } = useFormContext();
  const { isOffline } = useAuth();

  const formValues = watch();
  const [address, setAddress] = useState(
    metadata?.defaultValue?.address || formValues.location?.address || false
  );

  const isDisabledRef = useRef(false); // Track disabled state using ref
  const [fieldMetadata, setFieldMetadata] = React.useState(metadata); // Store metadata in state
  const [showModal, setShowModal] = useState(false);
  const [uploadedGeoJSONLayer, setUploadedGeoJSONLayer] = useState(
    metadata.type == 'coordinates' ? metadata.defaultValue : null
  );
  const [locationFetched, setLocationFetched] = useState(false); // Track if the location was fetched already
  const [geoLocationCompleted, setGeoLocationCompleted] = useState(false);

  useEffect(() => {
    //to show that address in the form form below the location field
    if (formValues[metadata.id]?.address) {
      setAddress(formValues[metadata.id].address);
    }
  }, [formValues[metadata.id]?.address]);

  // Sync isEditable from props
  useEffect(() => {
    setFieldMetadata(prev => {
      if (prev.isEditable !== metadata.isEditable) {
        return { ...prev, isEditable: metadata.isEditable };
      }
      return prev;
    });
  }, [metadata.isEditable]);

  // Simple effect to update char_length
  useEffect(() => {
    if (metadata.type === 'char_length' && metadata.dependsOn) {
      const dependentValue = watch(metadata.dependsOn) || '';
      setValue(metadata.id, dependentValue.length);
    }
  }, [watch(metadata.dependsOn)]);

  useEffect(() => {
    if (metadata.type === 'number' && metadata.dependsOn) {
      const dependentValue = watch(metadata.dependsOn);

      if (dependentValue) {
        let coords = null;
        if (Array.isArray(dependentValue.geometry?.coordinates)) {
          coords = dependentValue.geometry.coordinates;
        } else if (
          dependentValue.type === 'Polygon' &&
          Array.isArray(dependentValue.coordinates)
        ) {
          coords = dependentValue.coordinates;
        }

        if (coords) {
          try {
            const polygon = turf.polygon(coords);
            const area = turf.area(polygon); // square meters

            let finalValue;
            if (
              metadata.label?.toLowerCase().includes('(ha)') ||
              metadata.label?.toLowerCase().includes('hectare')
            ) {
              finalValue = (area / 10000).toFixed(2);
            } else {
              // Default to Acres if not specifically Ha
              finalValue = (area / 4046.8564224).toFixed(2);
            }

            setValue(metadata.id, finalValue || 0, { shouldValidate: true });
          } catch (e) {
            console.warn('Area calculation failed:', e);
          }
        }
      }
    }
  }, [watch(metadata.dependsOn), metadata.label, metadata.id]);

  // Age calculation logic
  useEffect(() => {
    if (metadata.id === 'age' && metadata.dependsOn === 'dob') {
      const dobValue = watch('dob');
      if (dobValue) {
        try {
          const birthDate = new Date(dobValue);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          const finalAge = age >= 0 ? age : 0;
          if (getValues('age') !== finalAge) {
            setValue('age', finalAge, { shouldValidate: true });
          }
        } catch (e) {
          console.error('Age calculation error:', e);
        }
      }
    }
  }, [watch('dob'), metadata.id, metadata.dependsOn, setValue, getValues]);

  // Force uppercase for NIN fields
  useEffect(() => {
    if (
      (metadata.label?.toLowerCase().includes('nin') ||
        metadata.id?.toLowerCase().includes('nin')) &&
      formValues[metadata.id] &&
      typeof formValues[metadata.id] === 'string' &&
      formValues[metadata.id] !== formValues[metadata.id].toUpperCase()
    ) {
      setValue(metadata.id, formValues[metadata.id].toUpperCase(), {
        shouldValidate: true
      });
    }
  }, [formValues[metadata.id], metadata.id, metadata.label, setValue]);

  // Add dependencies

  const isFieldDisabled = () => {
    // Check if the field has a `disables` array
    if (fieldMetadata.disables?.length > 0) {
      // Iterate through the disables array
      for (const disabledField of fieldMetadata.disables) {
        if (formValues[disabledField]) {
          // If any field in disables has a value, disable the field
          if (!isDisabledRef.current) {
            isDisabledRef.current = true; // Set ref to true to indicate the field should be disabled
            setFieldMetadata(prevMetadata => ({
              ...prevMetadata,
              isEditable: false // Disable the field
            }));
          }
          return true; // Field is disabled
        }
      }
    }

    // If no disabling condition is met, make sure it's editable
    if (isDisabledRef.current) {
      isDisabledRef.current = false; // Reset the ref
      setFieldMetadata(prevMetadata => ({
        ...prevMetadata,
        isEditable: true // Enable the field
      }));
    }

    return false; // Field is not disabled
  };

  useEffect(() => {
    if (metadata.type === 'char_length' && metadata.dependsOn) {
      // Subscribe to changes in the dependent field
      const subscription = watch((value, { name }) => {
        if (name === metadata.dependsOn) {
          const textLength = (value[metadata.dependsOn] || '').length;
          setValue(metadata.id, textLength.toString(), {
            shouldValidate: true
          });
        }
      });

      // Initial value setup
      const initialValue = getValues(metadata.dependsOn);
      if (initialValue) {
        setValue(metadata.id, initialValue.length.toString(), {
          shouldValidate: true
        });
      }

      return () => subscription.unsubscribe();
    }
  }, [
    metadata.type,
    metadata.dependsOn,
    watch,
    setValue,
    getValues,
    metadata.id
  ]);

  const dependentFieldValue = metadata.dependsOn
    ? Array.isArray(metadata.dependsOn)
      ? metadata.dependsOn.map(field => watch(field))
      : watch(metadata.dependsOn)
    : null;

  // Debounce the API call to prevent rapid successive calls
  const debouncedFetchData = useCallback(
    debounce(async values => {
      try {
        const requestData = {
          farmer_group: values[0] || [],
          village: values[1] || []
        };

        const response = await apiClient.post(
          `/digitization/v1/farmer/count`,
          requestData
        );

        setValue(metadata.id, response.data.results || '0');
      } catch (error) {
        console.error('Error fetching farmer count:', error);
        setValue(metadata.id, '0');
      }
    }, 500),
    [metadata.id]
  );

  const fetchDependentData = async () => {
    if (!metadata.dependsOn) {
      return;
    }
    // Handle multiple dependencies for total_farmer_count
    if (metadata.id === 'total_farmer_count') {
      const [farmerGroupValue, villageValue] = dependentFieldValue;
      // Skip if both values are empty
      if (!farmerGroupValue?.length && !villageValue?.length) {
        setValue(metadata.id, '');
        return;
      }
      // Use debounced function for farmer count
      debouncedFetchData([farmerGroupValue, villageValue]);
      return;
    }

    // Check if we're offline
    const isOnline = navigator.onLine;

    // Initialize dependentFields to collect parent field values
    let dependentFields = {};

    // Handle other dependent fields
    try {
      let endpoint = metadata.optionsEndpoint || metadata.endpoint;
      let requestData = {};

      // Function to get the value of dependsOn field
      const getValue = fieldId => {
        const field = document.querySelector(`[name="${fieldId}"]`);
        if (field && field.value) {
          return field.value;
        }
        // If field is not rendered, use form value (for Edit Mode)
        if (formValues && formValues[fieldId]) {
          return formValues[fieldId];
        }
        return null;
      };

      // Collect dependent field values for both API endpoint and offline use
      if (metadata.dependsOn) {
        if (Array.isArray(metadata.dependsOn)) {
          metadata.dependsOn.forEach(key => {
            const value = getValue(key);
            if (value) {
              // Store for offline use - this is the ID we'll use to find options
              dependentFields[key] = value;
              // Replace in endpoint for API use
              endpoint = endpoint.replace(`{${key}}`, value);
            }
          });
        } else {
          const value = getValue(metadata.dependsOn);
          if (value) {
            // Store for offline use - this is the ID we'll use to find options
            dependentFields[metadata.dependsOn] = value;
            // Replace in endpoint for API use
            endpoint = endpoint.replace(`{${metadata.dependsOn}}`, value);
          }
        }
      }

      // Handle case where {id} exists in URL
      if (endpoint.includes('{id}') && dependentFieldValue) {
        endpoint = endpoint.replace('{id}', dependentFieldValue);
      }

      // Prevent unnecessary API calls if value is empty
      if (
        (!dependentFieldValue ||
          (Array.isArray(dependentFieldValue) &&
            !dependentFieldValue.length)) &&
        !formValues[metadata.id]
      ) {
        return;
      }

      // Build requestData for multiSelect
      if (metadata.multiSelect && Array.isArray(dependentFieldValue)) {
        requestData = dependentFieldValue;
      } else if (metadata.multiSelect) {
        requestData = { [metadata.id]: [dependentFieldValue] };
      }

      let response;

      // Check if offline - if so, get data from Dexie DB
      if (!isOnline) {
        // console.log('Network offline, using local data from Dexie DB');
        response = await getOfflineData(
          metadata,
          dependentFields,
          dependentFieldValue
        );
      } else {
        // Online - call API based on method (POST/GET)
        if (metadata.multiSelect && Array.isArray(dependentFieldValue)) {
          response = await apiClient.post(
            `/digitization/v1${endpoint}`,
            requestData
          );
        } else {
          response = await apiClient.get(`/digitization/v1${endpoint}`);
        }
      }

      // Handle dropdown response
      if (metadata.type === 'dropdown') {
        const results = response.data.results;
        let options = Array.isArray(results) ? results : results.options || [];
        let serverDefaultValue = results.defaultValue;

        const updatedMetadata = {
          ...metadata,
          options: options
        };
        setFieldMetadata(updatedMetadata);

        // Automatically set value in Edit Mode OR if server provided a defaultValue
        if (serverDefaultValue !== undefined && serverDefaultValue !== null) {
          setValue(metadata.id, serverDefaultValue, { shouldValidate: true });
        } else if (formValues[metadata.id]) {
          setValue(metadata.id, formValues[metadata.id]);
        } else {
          setValue(metadata.id, updatedMetadata.defaultValue || '');
        }
      }

      // Handle textarea response
      else if (metadata.type === 'textarea') {
        const results = response.data.results;
        const val =
          results?.defaultValue !== undefined ? results.defaultValue : results;
        setValue(metadata.id, val);
      }
    } catch (error) {
      console.error('Error fetching dependent data:', error);

      // If error occurred, try to get data from offline storage as fallback
      if (metadata.type === 'dropdown') {
        try {
          const offlineResponse = await getOfflineData(
            metadata,
            dependentFields,
            dependentFieldValue
          );

          if (offlineResponse && offlineResponse.data.results) {
            // Update the field with offline data
            const updatedMetadata = {
              ...metadata,
              options: offlineResponse.data.results
            };
            setFieldMetadata(updatedMetadata);

            if (formValues[metadata.id]) {
              setValue(metadata.id, formValues[metadata.id]);
            } else {
              setValue(metadata.id, updatedMetadata.defaultValue || '');
            }
            return;
          }
        } catch (offlineError) {
          console.error('Error fetching from offline storage:', offlineError);
        }

        // If both online and offline attempts failed, clear options
        const updatedMetadata = {
          ...metadata,
          options: []
        };
        setFieldMetadata(updatedMetadata);
        setValue(metadata.id, metadata.multiSelect ? [] : '');
      }
    }
  };
  // Function to get data from Dexie DB when offline
  const getOfflineData = async (
    metadata,
    dependentFields,
    dependentFieldValue
  ) => {
    try {
      // Initialize results array
      let results = [];

      // Get the field ID from metadata
      const fieldId = metadata.id;

      // Get dropdown data from Dexie
      const dropdownData = await offlineDBService.getDropdownData();

      // Handle case when no dropdown data exists
      if (!dropdownData || !dropdownData.results) {
        console.error('No dropdown data available in Dexie DB');

        // Return empty results instead of throwing error
        return {
          data: {
            success: true,
            status: 200,
            results: []
          }
        };
      }

      // Check if the field exists in dropdownData
      if (dropdownData.results[fieldId]) {
        const fieldData = dropdownData.results[fieldId];

        // If the field depends on another field
        if (metadata.dependsOn) {
          let parentId;

          // Get the parent field ID
          if (Array.isArray(metadata.dependsOn)) {
            // Use the first valid dependency value
            for (const dep of metadata.dependsOn) {
              if (dependentFields[dep]) {
                parentId = dependentFields[dep];
                break;
              }
            }
          } else {
            parentId = dependentFields[metadata.dependsOn];
          }

          // Get options based on parent ID
          if (parentId && fieldData[parentId]) {
            results = fieldData[parentId];
          } else if (fieldData['all']) {
            // If no specific parent data but we have 'all' data
            results = fieldData['all'];
          } else {
            // If parent ID not found but we have data structure,
            // use the first available parent as fallback
            if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
              const firstParentId = Object.keys(fieldData)[0];
              if (firstParentId) {
                results = fieldData[firstParentId];
              } else {
                results = [];
              }
            } else if (Array.isArray(fieldData)) {
              // If it's directly an array, use it
              results = fieldData;
            }
          }
        } else {
          // Handle fields without dependencies

          // Check if it's a nested structure or direct array
          if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
            // It's a nested structure, flatten all values
            results = Object.values(fieldData).flat();
            // console.log(
            //   `Flattened ${results.length} options from nested structure`
            // );
          } else if (Array.isArray(fieldData)) {
            // It's already an array
            results = fieldData;
            // console.log(`Using ${results.length} options from direct array`);
          }
        }
      } else {
        console.log(`Field ${fieldId} not found in offline dropdown data`);
      }

      // Create a response object similar to API response
      const response = {
        data: {
          success: true,
          status: 200,
          results: results
        }
      };

      // console.log(`Returning ${results.length} options for ${fieldId}`);
      return response;
    } catch (error) {
      console.error('Error in getOfflineData:', error);

      // Return empty results instead of throwing error
      return {
        data: {
          success: true,
          status: 200,
          results: []
        }
      };
    }
  };
  // Memoize the dependency array to prevent unnecessary re-renders
  const deps = useMemo(() => {
    if (Array.isArray(metadata.dependsOn)) {
      return dependentFieldValue;
    }
    return [dependentFieldValue];
  }, [dependentFieldValue]);

  useEffect(() => {
    if (!geoLocationCompleted) return; // Wait until geolocation is finished

    if (!metadata.dependsOn) return;

    if (Array.isArray(metadata.dependsOn)) {
      const hasValidDependencies = deps.some(value =>
        Array.isArray(value) ? value.length > 0 : Boolean(value)
      );

      if (hasValidDependencies) {
        fetchDependentData();
      }
    } else if (deps[0] && (metadata.endpoint || metadata.optionsEndpoint)) {
      fetchDependentData();
    }
  }, [
    geoLocationCompleted,
    ...deps,
    metadata.id,
    metadata.endpoint,
    metadata.optionsEndpoint
  ]);

  useEffect(() => {
    const presetLocation = async () => {
      try {
        if (locationFetched) {
          setGeoLocationCompleted(true);
          return;
        }

        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            async position => {
              const { latitude, longitude, accuracy } = position.coords;
              // Restrict accuracy to 4 decimal places if needed
              const formattedAccuracy = Number(accuracy.toFixed(4));

              try {
                const addressDetails = await fetchAddressFromOla(
                  latitude,
                  longitude,
                  apiKey
                );

                if (metadata?.defaultValue?.lat) {
                  // Format default accuracy to 4 decimal places if it exists
                  const defaultAccuracy = metadata?.defaultValue?.accuracy
                    ? Number(metadata?.defaultValue?.accuracy.toFixed(4))
                    : undefined;

                  setValue(metadata.id, {
                    lat: metadata?.defaultValue?.lat,
                    lng: metadata?.defaultValue?.lng,
                    accuracy: defaultAccuracy,
                    address: metadata?.defaultValue?.address
                  });
                } else {
                  setValue(metadata.id, {
                    lat: latitude,
                    lng: longitude,
                    accuracy: formattedAccuracy,
                    address: addressDetails.address
                  });
                }

                setLocationFetched(true);
              } catch (error) {
                console.error('Error fetching address:', error);
              }
              setGeoLocationCompleted(true); // Mark geolocation as complete
            },
            error => {
              console.error('Error fetching location:', error);
              alert(
                'Failed to retrieve your location. Please ensure location services are enabled.'
              );
              setGeoLocationCompleted(true); // Mark geolocation as complete even on failure
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        } else {
          console.warn('Geolocation is not supported by this browser.');
          setGeoLocationCompleted(true); // Mark complete if geolocation is not available
        }
      } catch (error) {
        console.error('Error during presetLocation:', error);
        setGeoLocationCompleted(true); // Mark complete if there's an error
      }
    };

    if (
      metadata.type === 'geo_location' &&
      !locationFetched &&
      mode !== 'edit' &&
      isMobileDevice()
    ) {
      presetLocation();
    } else {
      setGeoLocationCompleted(true); // If no geolocation needed, mark as complete
      if (metadata?.defaultValue?.lat) {
        // Format default accuracy to 4 decimal places if it exists
        const defaultAccuracy = metadata?.defaultValue?.accuracy
          ? Number(metadata?.defaultValue?.accuracy.toFixed(4))
          : undefined;

        setValue(metadata.id, {
          lat: metadata?.defaultValue?.lat,
          lng: metadata?.defaultValue?.lng,
          accuracy: defaultAccuracy,
          address: metadata?.defaultValue?.address
        });
      }
    }
  }, [
    metadata.type,
    metadata.id,
    setValue,
    apiKey,
    fetchAddressFromOla,
    locationFetched
  ]);

  const { field } = useController({
    name: metadata.id,
    control,
    defaultValue: metadata?.defaultValue
  });

  const handleLocationSelect = result => {
    if (result.type === 'location') {
      // Get accuracy value, default to 10 if not provided
      let accuracyValue = result.data.accuracy || 10;

      // Restrict accuracy to 4 decimal places
      accuracyValue = Number(accuracyValue.toFixed(4));

      // Ensure accuracy is included in the location data
      const locationData = {
        ...result.data,
        accuracy: accuracyValue
      };
      setValue(metadata.id, locationData); // Set the coordinates in the form with accuracy
    } else if (result.type === 'polygon') {
      if (result.data.geometry) {
        setUploadedGeoJSONLayer(result.data);
        const polygon = turf.polygon(result?.data?.geometry?.coordinates);
        const area = turf.area(polygon); // Area in square meters
        setValue(metadata.id, result.data);
      } else {
        const area = 0;
        setValue(metadata.id, {});
        setUploadedGeoJSONLayer({});
      }
    }
  };

  useEffect(() => {
    if (metadata.type === 'tableAggregation') {
      if (!modalDataMap || Object.keys(modalDataMap).length === 0) {
        setValue(metadata.id, 0);
      } else if (
        !modalDataMap[metadata?.tableId] ||
        modalDataMap[metadata?.tableId].length === 0
      ) {
        setValue(metadata.id, 0);
      } else {
        const total = modalDataMap[metadata.tableId].reduce((acc, value) => {
          return acc + Number(value[metadata.dependsOn] || 0);
        }, 0);

        setValue(metadata.id, total);
      }
    }
  }, [modalDataMap]);

  useEffect(() => {
    if (metadata.type == 'radio_group' && hideFields) {
      hideFields(metadata.hide, metadata.defaultValue);
    }
  }, []); // Runs when the component mounts

  const CalculationField = ({ metadata }) => {
    const {
      watch,
      setValue,
      register,
      formState: { errors }
    } = useFormContext();

    // Watch all dependent fields
    const dependentValues = watch(metadata.dependsOn);
    const currentValue = watch(metadata.id);

    useEffect(() => {
      // Skip calculation if dependent values aren't ready
      if (
        !dependentValues ||
        !dependentValues.every(val => val !== undefined && val !== '')
      ) {
        if (currentValue !== null) {
          setValue(metadata.id, null, {
            shouldDirty: true,
            shouldValidate: true
          });
        }
        return;
      }

      try {
        // Calculate the result
        const results = eval(metadata?.calculation);

        // Only update if the result has changed
        if (results !== currentValue) {
          setValue(metadata.id, results, {
            shouldDirty: true,
            shouldValidate: true
          });
        }
      } catch (error) {
        console.error('Calculation error:', error);
        setValue(metadata.id, null);
      }
    }, [dependentValues, metadata.id, metadata.calculation, setValue]);

    return (
      <Col>
        <Form.Group as={Col} sm={12} md={12} className="mb-3">
          <FormLabel
            label={metadata.label}
            required={metadata?.validation?.required}
            htmlFor={metadata.id}
          />
          <Form.Control
            id={metadata.id}
            {...register(metadata.id)}
            type="number"
            placeholder={metadata.placeholder}
            isInvalid={!!errors[metadata.id]}
            disabled={!metadata.isEditable}
          />
          <ErrorFeedback error={errors[metadata.id]} />
        </Form.Group>
      </Col>
    );
  };

  // Render form field based on metadata type
  const renderFormField = () => {
    switch (metadata.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tableAggregation':
      case 'input': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              {metadata.id?.toLowerCase().includes('phone') ||
              metadata.id?.toLowerCase().includes('mobile') ? (
                <InputGroup>
                  <InputGroup.Text id="basic-addon1">+256</InputGroup.Text>
                  <Form.Control
                    {...field}
                    {...register(metadata.id)}
                    type={metadata.type}
                    placeholder={metadata.placeholder}
                    isInvalid={!!errors[metadata.id]}
                    disabled={!fieldMetadata.isEditable}
                  />
                </InputGroup>
              ) : (
                <Form.Control
                  {...field}
                  {...register(metadata.id)}
                  type={metadata.type}
                  placeholder={metadata.placeholder}
                  isInvalid={!!errors[metadata.id]}
                  disabled={!fieldMetadata.isEditable}
                />
              )}
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      case 'minmax': {
        return (
          <Col>
            <Form.Group
              as={Col}
              sm={12}
              md={12}
              className="mb-1"
              style={{
                border: '1px solid grey',
                borderRadius: '5px',
                padding: '10px',
                position: 'relative'
              }}
            >
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Row>
                <Col sm={6}>
                  <Form.Control
                    {...register(`${metadata.id}.min`)}
                    type="number"
                    placeholder={`Min ${metadata.placeholder}`}
                    isInvalid={!!errors[metadata.id]?.min}
                    disabled={!metadata.isEditable}
                    onWheel={e => e.target.blur()}
                    // Use controlled input consistently
                    value={field.value?.min ?? ''}
                    onChange={e => {
                      const val = e.target.value;
                      field.onChange({
                        min: val === '' ? null : Number(val),
                        max: field.value?.max ?? null
                      });
                    }}
                  />
                </Col>
                <Col sm={6}>
                  <Form.Control
                    {...register(`${metadata.id}.max`)}
                    type="number"
                    placeholder={`Max ${metadata.placeholder}`}
                    isInvalid={!!errors[metadata.id]?.max}
                    disabled={!metadata.isEditable}
                    onWheel={e => e.target.blur()}
                    // Use controlled input consistently
                    value={field.value?.max ?? ''}
                    onChange={e => {
                      const val = e.target.value;
                      field.onChange({
                        min: field.value?.min ?? null,
                        max: val === '' ? null : Number(val)
                      });
                    }}
                  />
                </Col>
              </Row>
            </Form.Group>
            {(errors[metadata.id]?.min || errors[metadata.id]?.max) && (
              <div className="text-danger small">
                {errors[metadata.id]?.min?.message ||
                  errors[metadata.id]?.max?.message}
              </div>
            )}
          </Col>
        );
      }

      case 'contact_number': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              {metadata.id?.toLowerCase().includes('phone') ||
              metadata.id?.toLowerCase().includes('mobile') ? (
                <InputGroup>
                  <InputGroup.Text id="basic-addon1">+256</InputGroup.Text>
                  <Form.Control
                    {...field}
                    {...register(metadata.id)}
                    type={'text'}
                    inputMode="numeric"
                    placeholder={metadata.placeholder}
                    isInvalid={!!errors[metadata.id]}
                    disabled={!fieldMetadata.isEditable}
                  />
                </InputGroup>
              ) : (
                <Form.Control
                  {...field}
                  {...register(metadata.id)}
                  type={'text'}
                  inputMode="numeric"
                  placeholder={metadata.placeholder}
                  isInvalid={!!errors[metadata.id]}
                  disabled={!fieldMetadata.isEditable}
                />
              )}
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      case 'textarea': {
        const textValue = field.value || '';

        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Form.Control
                as="textarea"
                {...field}
                value={textValue}
                onChange={e => {
                  field.onChange(e);
                  if (metadata.showCharLength) {
                    setValue(`${metadata.id}_length`, e.target.value.length);
                  }
                }}
                style={{ minHeight: '100px' }}
                placeholder={metadata.placeholder}
                isInvalid={!!errors[metadata.id]}
                disabled={!fieldMetadata.isEditable}
              />
              {metadata.showCharLength && textValue.length > 0 && (
                <div className="text-end mt-1 text-muted">
                  Characters: {textValue.length}
                </div>
              )}
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }
      case 'numbercalculation': {
        return <CalculationField metadata={metadata} formValues={formValues} />;
      }

      case 'radio_group': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12}>
              <div className="mb-2">
                <FormLabel
                  label={metadata.label}
                  required={metadata?.validation?.required}
                />
              </div>
              <div className="d-flex flex-column">
                {metadata.options.map(option => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`${metadata.id}-${option.value}`}
                    label={option.label}
                    value={option.value}
                    disabled={!metadata.isEditable}
                    isInvalid={!!errors[metadata.id]}
                    {...register(metadata.id, {
                      onChange: e => {
                        if (metadata.hide && hideFields) {
                          hideFields(metadata.hide, e.target.value);
                        }
                      }
                    })}
                    defaultChecked={option.value === metadata.defaultValue}
                    className="d-inline-block mb-0"
                  />
                ))}
              </div>
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      // case 'repetitiveValueDropdown': {
      //   return (
      //     <DynamicDropDown
      //       metadata={metadata}
      //       control={control}
      //       errors={errors}
      //       dependentFieldValue={dependentFieldValue}
      //       enableSelectAll={true}
      //       isDisabled={isFieldDisabled()}
      //     />
      //   );
      // }

      // case 'searchDropdown': {
      //   return (
      //     <Form.Group as={Col} sm={12} md={12} className="mb-3">
      //       <FormLabel
      //         label={metadata.label}
      //         required={metadata?.validation?.required}
      //       />

      //       <Controller
      //         name={metadata.id} // Unique field name
      //         control={control} // React Hook Form control
      //         render={({ field }) => (
      //           <Select
      //             {...field} // Connects with React Hook Form
      //             options={metadata.options}
      //             isClearable={true}
      //             placeholder="Select Crop"
      //             classNamePrefix="react-select"
      //             isMulti={true}
      //             closeMenuOnSelect={false}
      //             styles={{
      //               control: base => ({
      //                 ...base,
      //                 minWidth: '200px', // Set min-width
      //                 maxWidth: '400px', // Optional max width
      //                 width: '100%' // Ensure it expands to available width
      //               })
      //             }}
      //           />
      //         )}
      //       />
      //       <ErrorFeedback key={metadata.id} error={errors[metadata.id]} />
      //     </Form.Group>
      //   );
      // }

      case 'dropdown': {
        // const isDisabled = isFieldDisabled(metadata.id);
        return (
          <DynamicDropDown
            metadata={fieldMetadata}
            control={control}
            errors={errors}
            dependentFieldValue={dependentFieldValue}
            enableSelectAll={true}
            isDisabled={isFieldDisabled()}
          />
        );
      }
      case 'toggle_with_dropdown':
        return (
          <ToggleWithDropdown
            metadata={metadata}
            control={control}
            errors={errors}
          />
        );
      case 'toggle': {
        const normalizedValue = field.value ?? metadata.defaultValue ?? false;

        return (
          <Form.Group key={metadata.id} as={Col} sm={12} className="mb-3">
            <FormLabel
              label={metadata.label}
              required={metadata?.validation?.required}
            />
            <ButtonGroup className="d-block">
              <ToggleButton
                disabled={!fieldMetadata.isEditable}
                {...field}
                id={`${metadata.id}-false`}
                type="radio"
                className="px-3"
                name="evaluationEnabled"
                variant={normalizedValue ? 'light' : 'primary'} // No if `normalizedValue` is true
                checked={!normalizedValue}
                onChange={() => {
                  field.onChange(false) && onToggleChange(false);
                }}
              >
                No
              </ToggleButton>

              <ToggleButton
                disabled={!fieldMetadata.isEditable}
                id={`${metadata.id}-true`}
                type="radio"
                className="px-3"
                name="evaluationEnabled"
                variant={normalizedValue ? 'primary' : 'light'} // Yes if `normalizedValue` is true
                checked={normalizedValue}
                onChange={() => {
                  field.onChange(true) && onToggleChange(true);
                }}
              >
                Yes
              </ToggleButton>
            </ButtonGroup>
          </Form.Group>
        );
      }

      case 'checkbox': {
        return (
          <CheckBox
            metadata={metadata}
            register={register}
            field={{ ...field }}
            errors={errors}
          />
        );
      }
      case 'sectionHeader': {
        // Simplified char_length rendering - it's now just a display field
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <h5>{metadata.label}</h5>
            </Form.Group>
          </Col>
        );
      }
      case 'file': {
        return (
          <File metadata={metadata} field={{ ...field }} setValue={setValue} />
        );
      }
      case 'date':
      case 'dob': {
        return (
          <Dob
            metadata={metadata}
            {...field}
            setValue={setValue}
            register={register}
            errors={errors}
            ErrorFeedback={ErrorFeedback}
          />
        );
        // return (
        //   <Col>
        //     <Form.Group as={Col} sm={12} md={12} className="mb-3">
        //       <FormLabel
        //         label={metadata.label}
        //         required={metadata?.validation?.required}
        //       />
        //       <Form.Control
        //         {...field}
        //         {...register(metadata.id)}
        //         type={'date'}
        //         placeholder={metadata.placeholder}
        //         isInvalid={!!errors[metadata.id]}
        //       />
        //       <ErrorFeedback error={errors[metadata.id]} />
        //     </Form.Group>
        //   </Col>
        // );
      }
      case 'geo_location': {
        return (
          <Col key={metadata.id}>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <div className="flex justify-between items-center">
                <FormLabel
                  label={metadata.label}
                  required={metadata?.validation?.required}
                />
                {!isOffline && (
                  <Button
                    style={{ marginLeft: '28px' }}
                    onClick={() => setShowModal(true)}
                    variant="primary"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-geo-alt-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" />
                    </svg>
                  </Button>
                )}
              </div>
              {formValues[metadata.id] && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px',
                    maxWidth: '300px',
                    borderRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#f9f9f9',
                    fontSize: '14px'
                  }}
                >
                  <div>
                    <h4
                      style={{
                        marginBottom: '6px',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Pinned Location
                    </h4>
                    {formValues[metadata.id]?.accuracy > 0 && (
                      <p style={{ margin: '4px 0', color: '#555' }}>
                        Accurate to{' '}
                        <strong>
                          {typeof formValues[metadata.id].accuracy === 'number'
                            ? formValues[metadata.id].accuracy.toFixed(4)
                            : formValues[metadata.id].accuracy}
                          m
                        </strong>
                      </p>
                    )}
                    {address ? (
                      <p style={{ margin: '4px 0', color: '#555' }}>
                        Address: {address}
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )}
              {!formValues[metadata.id]?.address && (
                <ErrorFeedback error={errors[metadata.id]} />
              )}
            </Form.Group>
            <MapModel
              defaultValue={metadata?.defaultValue} //passing the default value which contain location details from backend so that the map can have dafault data
              showModal={showModal}
              setShowModal={setShowModal}
              mode="location"
              onLocationSelect={handleLocationSelect}
            />
          </Col>
        );
      }
      case 'coordinates': {
        return (
          <Col key={metadata.id}>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Button onClick={() => setShowModal(true)} variant="primary">
                Map Farm-land
              </Button>
              {(formValues[metadata.id]?.geometry?.coordinates ||
                metadata.defaultValue?.geometry?.coordinates) && (
                <div className="mt-2">Plotted</div>
              )}

              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
            <MapModel
              key={metadata.id}
              showModal={showModal}
              setShowModal={setShowModal}
              mode="polygon"
              uploadedGeoJSONLayer={uploadedGeoJSONLayer}
              onLocationSelect={handleLocationSelect}
            />
          </Col>
        );
      }
      case 'date_range':
        return (
          <RangeDatePicker
            onDateChange={handleDateRangeChange}
            noLabel={false}
            styleWidth={true}
          />
        );
      case 'month_day_range':
        return (
          <MonthDayRange
            onRangeChange={handleMonthDayChange}
            styleWidth={true}
            metadata={metadata}
          />
        );
      case 'password': {
        return (
          <div>
            <PasswordInput metadata={metadata} {...field} />
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      {renderFormField()}
      {/* Add the MapModal here */}
    </>
  );
};

export default DynamicNormalForm;
